import type { LucideIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Icon } from './icon';

/**
 * 보여줄 것이 없을 때 — 이유 한 줄과 주 행동 하나.
 *
 * 문구는 S 문서와 AC가 정한다(E14 §13) — 이 컴포넌트는 조판만 맡는다.
 * 빈 상태는 `Disclosure` 안에 접지 않는다. 항상 보인다(D3 §4.6). 일러스트를 두지 않는다.
 * 손으로 쓰던 65곳 중 `ink-faint`(2.3:1)는 D2가 의미 있는 텍스트에 금지한 색이다.
 */
export interface EmptyStateProps extends Omit<ComponentProps<'p'>, 'title'> {
  /** 무엇이 비었는지 한 줄. 없으면 `children`(이유)만 남는다. */
  title?: ReactNode;
  /** 선택. 빈 상태의 성격을 알리는 아이콘 하나(`Icon lg`, `ink-muted`). */
  icon?: LucideIcon;
  /**
   * 주 행동 **하나** — 처음이라 비었으면 만들기, 조건 때문이면 조건을 바꾸는 행동.
   * 보조 행동은 이유 문구 안의 링크 한 줄로 둔다.
   */
  action?: ReactNode;
}

export function EmptyState({
  title,
  icon,
  action,
  children,
  className,
  ...props
}: EmptyStateProps) {
  if (title === undefined && icon === undefined && action === undefined) {
    return (
      <p className={cn('text-ink-muted text-sm', className)} {...props}>
        {children}
      </p>
    );
  }

  return (
    <div className={cn('gap-row flex items-start', className)}>
      {icon ? <Icon as={icon} size="lg" className="text-ink-muted" /> : null}
      <div className="gap-row flex min-w-0 flex-col items-start">
        <div className="gap-tight flex flex-col">
          {title !== undefined ? <p className="text-fg text-sm font-medium">{title}</p> : null}
          {children !== undefined ? (
            <p className="text-ink-muted text-sm" {...props}>
              {children}
            </p>
          ) : null}
        </div>
        {action}
      </div>
    </div>
  );
}
